/**
 * TableAnswerInput Component
 * BUG-20260224-180125: Structured form input for table-type questions.
 * Auto-appears when a table question is detected in the current question cycle.
 * Supports single-column (type_10, type_3b) and true-false (type_8) layouts.
 * Modified by US-002: added isShowingTip prop to gate input while learning-tip popup is open.
 * Modified by US-008: added multi-column layout for graphic-organiser questions
 *   (type_14/15/16); adds renderMultiColumnRows(), multiValues state, and row-major submit.
 *   ConversationPanel.tsx required NO change — existing hasMarkdownTable dispatch covers organisers.
 */

import React, { useState, useEffect, useRef } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useViewportHeight } from '@/hooks/useViewportHeight';
import type { ParsedTable } from '@/lib/tableParser';

export interface TableAnswerInputProps {
  /** Parsed table structure from the tutor's question */
  table: ParsedTable;
  /** Callback when student submits their answer */
  onSubmit: (content: string) => void;
  /** True while submission is in progress */
  isSubmitting: boolean;
  /** True while waiting for tutor response after submission */
  isWaitingForTutor?: boolean;
  /** Pre-filled values from student's previous attempt (rowLabel → column values) */
  previousValues?: Map<string, string[]>;

  /** True while a learning-tip popup is open — disables the table input (US-002) */
  isShowingTip?: boolean;
}

/**
 * TableAnswerInput - Structured form for table-type comprehension questions
 * Implements US-008 T5: multi-column layout added alongside existing single-column / true-false.
 */
export function TableAnswerInput({
  table,
  onSubmit,
  isSubmitting,
  isWaitingForTutor = false,
  previousValues,
  // US-002: gate table input while learning-tip popup is open (AC-1/AC-2/AC-3).
  // Mirrors the isWaitingForTutor gate already used by isDisabled + canSubmit.
  isShowingTip = false,
}: TableAnswerInputProps) {
  useViewportHeight();

  const inputRows = table.rows.filter((r) => !r.isExample);
  const exampleRows = table.rows.filter((r) => r.isExample);
  const firstInputRef = useRef<HTMLInputElement>(null);
  const firstSelectRef = useRef<HTMLSelectElement>(null);

  // BUG-20260731-163953 (RC-006, T-19, 2026-07-31): a pre-filled table was
  // submittable with ZERO student interaction — the display defect this bug
  // is about (a stale/mismatched widget) then destroyed a metered attempt
  // budget, because every Submit silently resent the byte-identical
  // prefilled values. hasInteracted is set true by every value-changing
  // handler across all three layouts (single-column, true-false including
  // the select, multi-column) and gates canSubmit below. The prefilled
  // values themselves are UNCHANGED — still displayed, still editable — this
  // only changes whether Submit is armed on mount.
  const [hasInteracted, setHasInteracted] = useState(false);
  const isPrefilled = (previousValues?.size ?? 0) > 0;

  // State for single-column: array of values indexed by input row
  const [singleValues, setSingleValues] = useState<string[]>(() =>
    inputRows.map((row) => previousValues?.get(row.rowLabel)?.[0] ?? '')
  );

  // State for true-false: array of { tf, reason } indexed by input row
  const [tfValues, setTfValues] = useState<Array<{ tf: string; reason: string }>>(() =>
    inputRows.map((row) => {
      const prev = previousValues?.get(row.rowLabel);
      return {
        tf: prev?.[0] ?? '',
        reason: prev?.[1] ?? '',
      };
    })
  );

  // US-008 T5 Edit 1: multi-column values [inputRowIndex][fillableColIndex].
  // Pre-filled cells (cell !== null) store their fixed value; blank cells (null) start from
  // previousValues (retry) or '' (fresh). This ensures row-major alignment (DD-LOCAL-4).
  // Implements US-008; enforces AC-008-5 (retry pre-fill initialisation).
  const [multiValues, setMultiValues] = useState<string[][]>(() =>
    inputRows.map((row) =>
      (row.prefilled ?? []).map((cell, c) =>
        cell !== null ? cell : (previousValues?.get(row.rowLabel)?.[c] ?? '')
      )
    )
  );

  // Re-initialize when previousValues change (retry with pre-fill)
  // US-008 T5 Edit 2: extend effect to also re-init multiValues on retry.
  useEffect(() => {
    if (previousValues && previousValues.size > 0) {
      setSingleValues(inputRows.map((row) => previousValues.get(row.rowLabel)?.[0] ?? ''));
      setTfValues(
        inputRows.map((row) => {
          const prev = previousValues.get(row.rowLabel);
          return { tf: prev?.[0] ?? '', reason: prev?.[1] ?? '' };
        })
      );
      // US-008: mirrors Edit 1 — re-init multi-column blank cells from retry previousValues.
      setMultiValues(
        inputRows.map((row) =>
          (row.prefilled ?? []).map((cell, c) =>
            cell !== null ? cell : (previousValues.get(row.rowLabel)?.[c] ?? '')
          )
        )
      );
      // BUG-20260731-163953 (RC-006, T-19): a NEW prefill (a new retry cycle,
      // or a newly-selected question prompt — see ConversationPanel's
      // identity-bound previousValues, DD-8) re-arms the interaction gate.
      // The student must touch something in THIS prefill before Submit is
      // enabled again, even if she had already interacted with a previous one.
      setHasInteracted(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previousValues]);

  // Auto-focus first input field
  // US-008 T5 Edit 3: multi-column falls into the 'else' branch — firstInputRef is attached
  // to the first blank cell in renderMultiColumnRows, so auto-focus is already covered.
  useEffect(() => {
    if (!isWaitingForTutor) {
      if (table.inputType === 'true-false') {
        firstSelectRef.current?.focus();
      } else {
        firstInputRef.current?.focus();
      }
    }
  }, [isWaitingForTutor, table.inputType]);

  // US-002: isShowingTip added so table cells and the Submit button are disabled
  // while a learning-tip popup is open — prevents answering a background-loaded question.
  const isDisabled = isSubmitting || isWaitingForTutor || isShowingTip;

  // Check if all required fields are filled
  // US-008 T5 Edit 4: multi-column canSubmit branch added above existing checks.
  const canSubmit = (() => {
    if (isDisabled) return false;
    // BUG-20260225-211448: Guard against vacuous truth — [].every() returns true, which would
    // incorrectly enable the submit button when there are no input rows. - 2026-02-25
    if (inputRows.length === 0) return false;
    // BUG-20260731-163953 (RC-006, T-19): a pre-filled table requires at
    // least one real student interaction before Submit arms, in ALL THREE
    // layouts (single-column, true-false, multi-column) — applied here,
    // ahead of the three layout-specific branches below, so it cannot be
    // missed on any of them the way a per-branch fix was on the original
    // defect (the RCA found the identical property in all three; had the
    // widget mismatch gone the other way, true-false would have failed
    // identically to single-column). The legitimate US-008 AC-008-5 retry
    // experience is preserved: prefilled values stay visible and editable —
    // only the ARMING of Submit changes.
    if (isPrefilled && !hasInteracted) return false;

    // US-008: multi-column — every blank cell (null in prefilled) must have student input.
    // Implements US-008; mirrors BUG-20260225-211448 vacuous-truth guard for hasAnyBlank.
    if (table.inputType === 'multi-column') {
      const hasAnyBlank = inputRows.some((row) =>
        (row.prefilled ?? []).some((cell) => cell === null)
      );
      if (!hasAnyBlank) return false; // nothing to fill -> keep disabled
      // BUG-20260721: index defensively. multiValues is state sized from the table this
      // instance MOUNTED with; ConversationPanel now keys the component per question so it
      // always matches, but an unkeyed mount elsewhere would otherwise read undefined here
      // and throw during render, blanking the whole app (no ErrorBoundary). Treating a
      // missing cell as empty degrades to a disabled Submit button instead.
      return inputRows.every((row, r) =>
        (row.prefilled ?? []).every(
          (cell, c) => cell !== null || (multiValues[r]?.[c] ?? '').trim().length > 0
        )
      );
    }

    if (table.inputType === 'true-false') {
      return tfValues.every((v) => v.tf.trim().length > 0 && v.reason.trim().length > 0);
    }
    return singleValues.every((v) => v.trim().length > 0);
  })();

  const handleSubmit = () => {
    if (!canSubmit) return;

    let content: string;

    // BUG-20260718-163555 (T-4, RC-001, 2026-07-18): multi-column submit — emit ONLY the
    // student's own BLANK-cell values, row-major, joined by '\n'. Pre-filled cells are
    // question-supplied content the student never typed and can never edit; the previous
    // AC-008-4/DD-LOCAL-4 contract emitted EVERY fillable cell (pre-filled included),
    // which leaked question text into the graded payload indistinguishably from the
    // student's own answer (RC-001) — the grading LLM then read the leaked pre-filled
    // echoes as the student wrongly re-filling boxes already given to her, an objection
    // that is unanswerable from her side because she never typed those lines.
    // Blank cells are still emitted in row-major order (blank-cell order, not all-cell
    // order) so the backend normaliser (format_student_answer_for_llm) and the retry
    // parser (parseStudentTableAnswer, tableParser.ts:347-359) can align them
    // positionally without labels on the wire (values-only precedent, BUG-20260310-195233
    // — see the single-column branch below). T-5 (tableParser.ts) is the matching change
    // to the retry parser and MUST ship in the same commit as this one.
    if (table.inputType === 'multi-column') {
      // BUG-20260721: defensive indexing, same rationale as canSubmit above. canSubmit already
      // gates this path, so a missing cell is unreachable in practice.
      content = inputRows
        .flatMap((row, r) =>
          (row.prefilled ?? []).map((cell, c) => (cell === null ? (multiValues[r]?.[c] ?? '') : null))
        )
        .filter((value): value is string => value !== null)
        .join('\n');
    } else if (table.inputType === 'true-false') {
      content = tfValues.map((v, i) => `${i + 1}. ${v.tf}. ${v.reason}`).join('\n');
    } else {
      // BUG-20260310-195233: Submit values only (no row label prefix) - 2026-03-10
      // Previously formatted as "${row.rowLabel}: ${singleValues[i]}", which caused the AI
      // to reject type_3b answers as multi-word (e.g., "task: challenge" → rejected, "challenge" → accepted).
      // Values are positionally matched: line 1 = first input row, line 2 = second input row, etc.
      content = inputRows.map((_row, i) => singleValues[i]).join('\n');
    }

    onSubmit(content);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey && canSubmit) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <div className="border-t border-[#E7D8C8] bg-otter-paper p-3 md:p-4 sticky bottom-0 z-10 pb-[env(safe-area-inset-bottom)]">
      <div className="max-w-3xl mx-auto">
        {/* Scrollable form area with max height.
            US-008 T5 Edit 8: increase max-h for multi-column organisers which can span more rows. */}
        <div
          className={cn(
            'overflow-y-auto space-y-2 mb-2',
            table.inputType === 'multi-column' ? 'max-h-[200px]' : 'max-h-[120px]'
          )}
        >
          {/* Example rows (read-only) */}
          {exampleRows.map((row) => (
            <div key={row.rowLabel} className="text-xs text-pebbles-500 italic px-1">
              Example: {row.rowLabel} → {row.prefilled?.join(', ')}
            </div>
          ))}

          {/* Input rows — dispatch to the correct layout renderer (US-008 T5 Edit 6). */}
          {table.inputType === 'multi-column'
            ? renderMultiColumnRows()
            : table.inputType === 'true-false'
              ? renderTrueFalseRows()
              : renderSingleColumnRows()}
        </div>

        {/* Waiting indicator */}
        {isWaitingForTutor && (
          <p className="text-sm text-pebbles-500 text-center mb-2">Waiting for tutor...</p>
        )}

        {/* Submit button */}
        <Button
          onClick={handleSubmit}
          disabled={!canSubmit}
          className="w-full h-11 md:h-10"
          aria-label="Submit answer"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Sending
            </>
          ) : (
            'Submit Answer'
          )}
        </Button>
      </div>
    </div>
  );

  function renderSingleColumnRows() {
    return inputRows.map((row, index) => (
      <div key={row.rowLabel} className="space-y-1">
        <label className="text-sm font-medium text-otter-earth-700 block px-1">
          {row.rowLabel}:
        </label>
        <Input
          ref={index === 0 ? firstInputRef : undefined}
          type="text"
          value={singleValues[index]}
          onChange={(e) => {
            const updated = [...singleValues];
            updated[index] = e.target.value;
            setSingleValues(updated);
            setHasInteracted(true);
          }}
          onKeyDown={handleKeyDown}
          disabled={isDisabled}
          placeholder={`Enter answer for ${row.rowLabel}`}
          className="h-11 md:h-10"
          aria-label={`Answer for ${row.rowLabel}`}
        />
      </div>
    ));
  }

  function renderTrueFalseRows() {
    return inputRows.map((row, index) => (
      <div
        key={row.rowLabel}
        className={cn('space-y-1 p-2 rounded-lg', index % 2 === 0 ? 'bg-otter-sand/30' : '')}
      >
        {/* Statement text */}
        <p className="text-xs text-otter-earth-700 font-medium leading-snug">{row.rowLabel}</p>

        {/* True/False dropdown + Reason input inline */}
        <div className="flex gap-2">
          <select
            ref={index === 0 ? firstSelectRef : undefined}
            value={tfValues[index].tf}
            onChange={(e) => {
              const updated = [...tfValues];
              updated[index] = { ...updated[index], tf: e.target.value };
              setTfValues(updated);
              setHasInteracted(true);
            }}
            disabled={isDisabled}
            className={cn(
              'flex h-9 md:h-8 w-[130px] shrink-0 rounded-[6px] border border-gray-300 bg-white px-2 py-1 text-xs',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-otter-orange-500 focus-visible:ring-offset-2',
              'disabled:cursor-not-allowed disabled:opacity-50',
              !tfValues[index].tf && 'text-gray-400'
            )}
            aria-label={`True or False for: ${row.rowLabel.substring(0, 50)}`}
          >
            <option value="">True / False</option>
            <option value="True">True</option>
            <option value="False">False</option>
          </select>

          <Input
            type="text"
            value={tfValues[index].reason}
            onChange={(e) => {
              const updated = [...tfValues];
              updated[index] = { ...updated[index], reason: e.target.value };
              setTfValues(updated);
              setHasInteracted(true);
            }}
            onKeyDown={handleKeyDown}
            disabled={isDisabled}
            placeholder="Type your reason..."
            className="h-9 md:h-8 flex-1 text-xs"
            aria-label={`Reason for: ${row.rowLabel.substring(0, 50)}`}
          />
        </div>
      </div>
    ));
  }

  /**
   * US-008 T5 Edit 7: Render multi-column organiser input rows.
   * Implements AC-008-1, AC-008-3, AC-008-4, AC-008-7.
   *
   * Layout: row label as heading, then one cell per fillable column.
   *   - Pre-filled cell (prefilled[c] !== null): read-only span (AC-008-3).
   *   - Blank cell (prefilled[c] === null): <Input> h-11 (AC-008-7) with aria-label
   *     "{columnHeader} for {rowLabel}" (AC-008-3 accessibility).
   *
   * firstInputRef is attached to the very first blank cell for auto-focus (T5 Edit 3).
   * Inputs update multiValues[r][c] on change (deep-ish copy — same pattern as singleValues).
   *
   * DO NOT touch renderTrueFalseRows or renderSingleColumnRows — those are the type_8/type_10
   * paths and must remain byte-identical (AC-008-6 + NFR-1).
   */
  function renderMultiColumnRows() {
    // Track whether we have already assigned firstInputRef to the first blank cell.
    let firstBlankAssigned = false;

    return inputRows.map((row, r) => (
      <div
        key={row.rowLabel}
        className={cn('space-y-1 p-2 rounded-lg', r % 2 === 0 ? 'bg-otter-sand/30' : '')}
      >
        {/* Row label (e.g. "Raj", "First discomfort", "Mood") */}
        <p className="text-sm font-medium text-otter-earth-700 leading-snug">{row.rowLabel}</p>

        {(row.prefilled ?? []).map((cell, c) => {
          const columnHeader = table.fillableHeaders[c] ?? `Column ${c + 1}`;
          if (cell !== null) {
            // Pre-filled cell → read-only text (AC-008-3).
            return (
              <div key={c} className="flex items-center gap-2">
                <span className="text-xs text-pebbles-500 w-28 shrink-0">{columnHeader}</span>
                <span className="text-sm text-otter-earth-700 flex-1 px-1 italic">{cell}</span>
              </div>
            );
          }
          // Blank cell → editable input (AC-008-3, AC-008-7).
          const attachRef = !firstBlankAssigned;
          if (attachRef) firstBlankAssigned = true;
          return (
            <div key={c} className="flex items-center gap-2">
              <span className="text-xs text-pebbles-500 w-28 shrink-0">{columnHeader}</span>
              <Input
                ref={attachRef ? firstInputRef : undefined}
                type="text"
                value={multiValues[r]?.[c] ?? ''}
                onChange={(e) => {
                  const next = multiValues.map((arr) => [...arr]); // deep-ish copy
                  // BUG-20260721: materialise the row if state is narrower than the table.
                  next[r] = next[r] ?? [];
                  next[r][c] = e.target.value;
                  setMultiValues(next);
                  setHasInteracted(true);
                }}
                onKeyDown={handleKeyDown}
                disabled={isDisabled}
                placeholder={`Enter ${columnHeader}`}
                className="h-11 md:h-10 flex-1"
                aria-label={`${columnHeader} for ${row.rowLabel}`}
              />
            </div>
          );
        })}
      </div>
    ));
  }
}
